// onboarding.ts — the pure state machine + script for the in-dashboard first-run
// "odyssey". No React, no I/O: importable by the server (data.ts reads the stored
// state) and the client conductor alike. Storage lives in `user_settings.onboarding`
// (migration 054); `user_metadata.onboarded` remains the terminal graduated bit.
//
// THE WALK (2026-10-22): a brand-new account arrives with the netti it was born
// with, netti descends to its seat (the Arrival), and the ONE thing asked before
// anything else opens is a connection: copy the invite, paste it into the ai you
// use most, and keep the connection when it calls through. Nobody enters before
// a connection is made and kept (THE GATE: the conductor, the progress route and
// the dashboard loader all hold it). Then `walk`, a short walk on the netti
// walkthrough engine (lib/walkthrough): the daily digest, the chat window, the
// you space with an example in it. Then `free` = everything unlocked, the
// account marked `onboarded`, and per-space first-visit intros after that.
//
// The stage ENUM only ever grows (stored states stay parseable): `projects`,
// `create` and `import` are parked stages with no beats, and a state stored at
// one of them is settled at boot (settleObState): no connection -> connections,
// a connection -> walk. Folder creation is not part of onboarding any more.

import { FIRST_RUN_WALK } from "../walkthrough/registry";
import type { TargetKey } from "../walkthrough/targets";

export const OB_VERSION = 1 as const;

export type ObStage =
  | "home"
  | "projects"
  | "create"
  | "connections"
  | "import"
  | "walk"
  | "free";

/** The ordered spine of stages — used to compare progress + step forward.
 *  Never remove a value (a stored state at it would stop parsing); `walk` sits
 *  just before `free`, so the forward-only progress route can never rewind a
 *  walk back into the connection step. */
export const OB_STAGES: ObStage[] = [
  "home",
  "connections",
  "projects",
  "create",
  "import",
  "walk",
  "free",
];

/** the parked stages: no beats, settled at boot (settleObState). */
export const LEGACY_STAGES: readonly ObStage[] = ["projects", "create", "import"];

/** The netti a person was born with on the birth page (/test/onboarding →
 *  the real signup). Carried into the account on first arrival so the creature
 *  under the dashboard is the SAME one they held into existence — never a second
 *  mint from their email. */
export interface ObNetti {
  /** the identity string the birth minted from (futureIdentity seed). */
  identity: string;
  /** THE PERSON'S name, not the creature's. The birth asks you to complete
   *  "this is ___'s netti", so what you type is what YOU are called here. It
   *  becomes the account's display name. Reading it as the netti's name is
   *  what once produced "bob is here, bob." */
  owner?: string;
}

export interface ObState {
  v: typeof OB_VERSION;
  /** the current stage of the flow. */
  stage: ObStage;
  /** index of the current script beat within the stage. */
  beat: number;
  /** panel key -> ISO timestamp of the first-visit intro being shown. */
  seen: Record<string, string>;
  /** the user skipped the "make your first project" step. */
  createSkipped?: boolean;
  /** when an import was kicked off (for the "building" affordance / resume). */
  importStartedAt?: string;
  startedAt: string;
  /** set when the flow reaches `free` (mirrors user_metadata.onboarded). */
  completedAt?: string;
  /** the user clicked "explore" — the free-stage greeting is done for good, so
   *  it can never re-narrate; only ambient per-space intros play from here. */
  dismissed?: boolean;
  /** netti's arrival (the descent + the spaces) has played. Until this is set, a
   *  brand-new account opens on the arrival rather than the dashboard; once
   *  set, the guided walk proper begins and the arrival never returns. */
  bornAt?: string;
  /** the walk was parked on a phone ("continue later on desktop"). bornAt stays
   *  unset so a desktop session replays the whole welcome from the arrival; a
   *  phone shows the home space with a quiet "finish on desktop" note instead
   *  of the tour. Cleared implicitly when the flow reaches `free`. */
  deferredAt?: string;
  /** the netti carried in from the birth page (see ObNetti). */
  netti?: ObNetti;
  /** the tool the person chose to wire first (a CONNECT_TOOLS id). */
  tool?: string;
}

/** Which spaces are unlocked at each stage. `"all"` = no locking. Keys are OsNode
 *  keys — panels (PanelKind) OR the "projects" section, so this is string-typed. */
export const UNLOCKS: Record<ObStage, string[] | "all"> = {
  // THE GATE: until a connection is made and kept, connections is the one space
  // that is open (the walk stands there from the arrival on). The parked stages
  // are settled at boot, but carry the same single door in case one slips by.
  home: ["connections"],
  connections: ["connections"],
  projects: ["connections"],
  create: ["connections"],
  import: ["connections"],
  // the walk after the connection opens exactly the places it shows: the
  // connection's own window, the project and skills it brings in, the digest and
  // the chat window (settings), you, and the river
  walk: ["connections", "projects", "skills", "settings", "you", "activity"],
  free: "all",
};

/** Each onboarding stage wears its own colour, so the first-run is a journey
 *  through the spectrum — netti blooms and every space glows a different hue —
 *  rather than one flat white screen. The hue feeds a ColorState in OsShell that
 *  drives the bloom + retints the whole UI for that stage. `free` = the user's
 *  own organism takes back over.
 *
 *  When the account carries a birth netti (`ObState.netti`) the shell keeps THAT
 *  creature's palette throughout and these hues are not used — the walk is in
 *  the colour they were born with. */
export const STAGE_HUE: Record<ObStage, number> = {
  home: 217, // netti blue — the bond
  projects: 268, // violet — the ideas space
  create: 30, // amber — making a thing
  connections: 168, // teal — wiring in
  import: 320, // magenta
  walk: 250, // the settings blue — the walk after the connection
  free: 140, // green — alive, settled
};

/** Per-space hue for the post-graduation first-visit intros (keyed by panel key),
 *  so opening a space for the first time also arrives in its own colour. */
export const SPACE_HUE: Record<string, number> = {
  projects: 268,
  skills: 96,
  connections: 168,
  // a soft rose, apart from you (322) and activity (24): the one hue no other space wears
  agents: 352,
  you: 322,
  netti: 200,
  // a deep sea blue, between netti (200) and the projects' violet: the memory itself
  memory: 228,
  activity: 24,
  tasks: 140,
  parked: 52,
  settings: 250,
};

/** A fresh state for an account entering the flow. `now` is passed in (no clock in a pure module). */
export function initialObState(now: string): ObState {
  return { v: OB_VERSION, stage: "home", beat: 0, seen: {}, startedAt: now };
}

/**
 * The set of unlocked panel keys for a stored state, or `null` when everything is
 * unlocked (no state, a completed flow, or an unknown/old version). `null` is the
 * signal buildRoot uses to skip locking entirely — backward-compatible by default.
 */
export function unlockedFor(s: ObState | null | undefined): ReadonlySet<string> | null {
  if (!s || s.v !== OB_VERSION) return null;
  const u = UNLOCKS[s.stage];
  if (u === "all") return null;
  return new Set<string>(u);
}

/** Is the flow still in progress (i.e. the conductor should run + locking applies)? */
export function isOnboarding(s: ObState | null | undefined): boolean {
  return !!s && s.v === OB_VERSION && s.stage !== "free";
}

/**
 * A stored state, settled against THE GATE (server and client read it alike).
 *  · a parked stage (projects · create · import) has no beats any more: it goes
 *    to `connections` while nothing is connected, and to `walk` once something is;
 *  · a stage past the connection step (walk, or a free that never graduated)
 *    without a connection is clamped back to `connections`.
 * Everything else is returned as it was (the same object, so nothing re-renders).
 */
export function settleObState(s: ObState, hasConnection: boolean): ObState {
  if (LEGACY_STAGES.includes(s.stage)) return { ...s, stage: hasConnection ? "walk" : "connections", beat: 0 };
  if (!hasConnection && (s.stage === "walk" || s.stage === "free")) return { ...s, stage: "connections", beat: 0 };
  return s;
}

/** Is a stage past the connection step (one that only a connection may reach)? */
export function needsConnection(stage: ObStage): boolean {
  return stage === "walk" || stage === "free";
}

/** The next stage after `stage`, or `stage` itself if already at the end. */
export function nextStage(stage: ObStage): ObStage {
  const i = OB_STAGES.indexOf(stage);
  return i < 0 || i >= OB_STAGES.length - 1 ? "free" : OB_STAGES[i + 1];
}

/** Coerce an unknown value read from the DB into a valid ObState or null. */
export function parseObState(raw: unknown): ObState | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.v !== OB_VERSION) return null;
  if (typeof r.stage !== "string" || !OB_STAGES.includes(r.stage as ObStage)) return null;
  const n = r.netti && typeof r.netti === "object" ? (r.netti as Record<string, unknown>) : null;
  const netti: ObNetti | undefined =
    n && typeof n.identity === "string" && n.identity
      ? {
          identity: n.identity,
          // `name` is the old key for the same value, kept readable so a state
          // stored before the rename still knows who its person is.
          ...(() => {
            const o = typeof n.owner === "string" && n.owner ? n.owner : typeof n.name === "string" ? n.name : "";
            return o ? { owner: o.slice(0, 40) } : {};
          })(),
        }
      : undefined;
  return {
    v: OB_VERSION,
    stage: r.stage as ObStage,
    beat: typeof r.beat === "number" ? r.beat : 0,
    seen: r.seen && typeof r.seen === "object" ? (r.seen as Record<string, string>) : {},
    createSkipped: r.createSkipped === true || undefined,
    importStartedAt: typeof r.importStartedAt === "string" ? r.importStartedAt : undefined,
    startedAt: typeof r.startedAt === "string" ? r.startedAt : "",
    completedAt: typeof r.completedAt === "string" ? r.completedAt : undefined,
    dismissed: r.dismissed === true || undefined,
    bornAt: typeof r.bornAt === "string" ? r.bornAt : undefined,
    deferredAt: typeof r.deferredAt === "string" ? r.deferredAt : undefined,
    ...(netti ? { netti } : {}),
    ...(typeof r.tool === "string" && r.tool ? { tool: r.tool.slice(0, 40) } : {}),
  };
}

// ── the script ────────────────────────────────────────────────────────────────
//
// Each beat is one line netti says in the guide. `advance` names how the beat is
// dismissed:
//   "next"   — the "next" chip.
//   "hover"  — the user hovers anything (a hint fires) — teaches the Noticer.
//   "action" — a labelled action chip drives it (e.g. "keep it as it is").
//   "auto"   — an external event advances it (the invite copied, a call arriving).
// `actions` are extra labelled chips beyond the default "next". THERE IS NO
// EXIT before a connection is kept: no "enter innernet", no skip. The one
// `enter` in the whole script is the walk's last beat (walk.done), and the
// conductor honours it nowhere else.
// `org` is an optional organism override key (the conductor maps it to a menuOrg-style choreo).
// `target` names a place on the page netti points at (lib/walkthrough/targets).
//
// `{name}` fills with the person's first name and `{tool}` with the ai that
// called through (or "your ai").

export type ObAdvance = "next" | "hover" | "action" | "auto";

export interface ObAction {
  label: string;
  /** a semantic id the conductor interprets (next · tools · keep · walk · enter · grant · grant-later · ground · done). */
  act: string;
}

export interface ObBeat {
  id: string;
  text: string;
  advance: ObAdvance;
  actions?: ObAction[];
  /** organism override key for this beat (conductor-mapped). */
  org?: string;
  /** show only on this viewport (default: both). */
  only?: "mobile" | "desktop";
  /** a place on the page netti points at while the beat is up (lib/walkthrough/targets). */
  target?: TargetKey;
}

/** the chip that leaves the invite for the connections space's tools list. */
const BY_HAND: ObAction = { label: "i'd rather do it by hand", act: "tools" };

/** The beats per stage, in order. The conductor plays the current stage's beats. */
export const SCRIPT: Record<Exclude<ObStage, "free">, ObBeat[]> = {
  // the arrival's "begin" goes straight to the connection: home says nothing of
  // its own (the stage keeps its stored name; old states must stay parseable).
  home: [],
  connections: [
    // ONE step before everything else. The connections space stands on its
    // invite view (components/connect/AutoConnect): the invite in a white card,
    // one copy, and netti listening for the first call.
    { id: "conn.invite", text: "one step before everything else: let one ai read your memory. copy the invite and paste it into the ai you use most. it sets itself up, or walks you through it.", advance: "auto", org: "connections-regrow", target: "invite-copy", actions: [BY_HAND] },
    // the invite was copied (innernet:invite-copied): netti waits for the call.
    { id: "conn.listening", text: "i'm listening. sign in when your ai asks, and i'll know the moment it calls.", advance: "auto", actions: [BY_HAND] },
    // it called through (innernet:connected). The card shows the ai's own icon
    // and its name, ready to edit; keeping it (innernet:connection-saved) opens
    // the walk. Keeping the offered name is a keep too: the quiet chip is that
    // keep for a connection made by hand (the tools list), where no card asks.
    { id: "conn.detected", text: "{tool} just called through. give it a name you'll recognise, then keep it.", advance: "action", target: "invite-keep", actions: [{ label: "keep it as it is", act: "keep" }] },
    // the tool got here FIRST — the account was made from inside it (the consent
    // page) or while pairing the terminal, so a connection was already standing
    // when the walk began. Nothing to wire; the stage greets what is here.
    { id: "conn.already", text: "an ai already reached your memory before you did. it can read it now, and you will find it here in connections whenever you want to name it.", advance: "action", actions: [{ label: "next", act: "walk" }] },
    // the tool that sent them here is still WAITING AT THE DOOR: they made the
    // account inside it, met their netti, and came straight here. Letting it in
    // finishes that authorization and returns them to it; waving it off keeps
    // the walk going and the tool asks again on its own.
    { id: "conn.waiting", text: "{tool} is at the door, waiting to be let in. say yes and it reads this memory from now on, and you go back to it.", advance: "action", actions: [
      { label: "let it in", act: "grant" },
      { label: "not now", act: "grant-later" },
    ] },
  ],
  // parked: no beats (settled at boot, see settleObState)
  projects: [],
  create: [],
  import: [],
  // the walk after the connection, played from the netti walkthrough registry
  // (lib/walkthrough/registry FIRST_RUN_WALK): one source for its words.
  walk: FIRST_RUN_WALK.beats.map((b) => ({
    id: b.id,
    text: b.say,
    advance: "action" as const,
    actions: b.acts?.map((a) => ({ label: a.label, act: a.act })) ?? [{ label: "next", act: "next" }],
    ...(b.target ? { target: b.target } : {}),
  })),
};

// ── per-space first-visit intros (post-free) ────────────────────────────────────
// Seeded from the SPACES one-liners + panel summaries. Keyed by PanelKind.

export const SPACE_INTROS: Record<string, string> = {
  projects: "every context map you build. one folder per idea, product or brand.",
  connections: "the ai tools reading and growing this same memory, live.",
  agents: "agents you build and tools you talk to, and what each has learned about you.",
  you: "who you are. your people, your tools, everything innernet holds for you.",
  netti: "the living body of your memory. watch it, and shape the ground it lives on.",
  memory: "your whole memory at a glance: how big it is, what reached it lately, and bringing more in.",
  activity: "everything touched, narrated. what your ais did, and what innernet did on its own.",
  tasks: "every open loop across all your projects, one running checklist.",
  skills: "what you can teach an ai. every page you keep that carries a skill, each one a link you can hand out.",
  parked: "ideas set aside on a branch. out of the way, still here.",
  settings: "your account, security, and the data innernet keeps for you.",
};

// ── the ground, handed over ──────────────────────────────────────────────────
// A new person's ground is tinted from the netti they were born with (bgId
// "minted", lib/theme mintedGround). Settings is where netti tells them, once,
// that the colour is theirs to change, with THE dial one press away (act
// `ground` → GROUND_DIAL_EVENT; never a second picker). Someone who has already
// picked a colour of their own hears the plain settings line instead.
export const SETTINGS_GROUND_INTRO: { text: string; actions: ObAction[] } = {
  text: "your account, and the data innernet keeps for you. your ground was tinted from your netti. please change it whenever you like.",
  actions: [{ label: "change colour", act: "ground" }],
};

/** What netti says on a first visit to a space (post-free), or null. */
export function spaceIntro(
  key: string,
  opts: { mintedGround?: boolean } = {},
): { text: string; actions?: ObAction[] } | null {
  if (key === "settings" && opts.mintedGround) return SETTINGS_GROUND_INTRO;
  const text = SPACE_INTROS[key];
  return text ? { text } : null;
}
